import { useMemo, useRef, useState } from 'react';

import { uniframeReady, webapi, isBrowser } from '@spot-fe/utils';
import { useI18n, useUserInfo } from '@spot-fe/base-ui';
import { useParams } from 'next/navigation';
import { message } from '@bfe/lux';
import { plus, safeToFormat1 as safeToFormat } from '@/utils/number';
import { PrizeType, TaskType, ProjectDetailV2 } from '@/apis';

interface IShareCommon {
  setProperty: (key: string, value: any) => IShareModal;
  subscribeEvent: (event: string, callback: any) => IShareModal;
  init: () => Promise<IShareModal>;
}
export interface IShareModal extends IShareCommon {
  // events: {
  //   HIDE_MODAL: string;
  //   REGISTER_SUCCESS: string;
  // };
  properties: {
    VISIBLE: string;
    CONFIG: string;
  };
}

// todo 确认
const urlUtm = 'sa_utm_ci=AD0003&utm_source=ref&sa_utm_channel=ref_';

const getLink = (code: string) => {
  if (!isBrowser) {
    return '';
  }
  const { href } = window.location;
  // const path = `/invite`;
  const hasQuery = href.indexOf('?') > -1;
  const linkWhole = `${href}${hasQuery ? '&' : '?'}ref=${code}`;
  return linkWhole;
};

export const getPrizeInfo = (detail?: ProjectDetailV2) => {
  // 分享奖励币种, 默认是交易币种
  let depositPrizeToken = '';
  let prizePool = '';
  let prizeToken = detail?.newUserPrizeToken;
  // 处理新老用户逻辑
  switch (detail?.prizeType) {
    case PrizeType.New:
      // 新用户项目
      depositPrizeToken = detail?.newUserPrizeToken;
      break;
    case PrizeType.Old:
      // 老用户项目
      depositPrizeToken = detail?.oldUserPrizeToken;
      break;
    default:
      // 通用项目
      depositPrizeToken = detail?.newUserPrizeToken || '';
  }
  // 处理项目类型逻辑
  switch (detail?.taskType) {
    case TaskType.Deposit:
      // 入金项目
      prizePool = plus(detail?.newUserPrizeTotal, detail?.oldUserPrizeTotal);
      prizeToken = depositPrizeToken;
      break;
    case TaskType.Trade:
      // 交易项目
      prizePool = detail?.tradeUserPrizeTotal;
      prizeToken = detail?.tradePrizeToken;
      break;
    default:
      // 入金+交易项目
      prizePool = plus(
        plus(detail?.newUserPrizeTotal, detail?.oldUserPrizeTotal),
        detail?.tradeUserPrizeTotal
      );
      prizeToken = depositPrizeToken;
  }

  return {
    prizePool,
    prizeToken,
  };
};

export function useShare() {
  const { t } = useI18n();
  const params = useParams();
  const { isLogin, userInfo } = useUserInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [readyState, setReadyState] = useState(-1);
  // const [code, setCode] = useState<string>();
  // const [_detail, setDetail] = useState<any>()
  const shareRef = useRef<IShareModal>();
  const inviteCode = useRef('');

  const getInviteCode = async () => {
    if (!isLogin) return;

    const res = await webapi.get<{
      affiliate_id: number;
      data: Array<{ code: string; status: string }>;
    }>('/s1/code/info-msg', { client_id: userInfo?.id, scene: 2 });
    const { success, result, message: msg } = res;

    if (!success) {
      message.error(msg);
      setIsLoading(false);
      return;
    }
    // 注意：只有状态为0的邀请码才可用
    if (
      result.data.length &&
      Number(result.data[0].status) === 0 &&
      result.data[0].code
    ) {
      inviteCode.current = result.data[0].code;
      // setCode(result.data[0].code)
    }
  };

  const init = async () => {
    await uniframeReady();
    return new Promise((resolve, reject) => {
      (window as any)?.BybitUniFrame?.ready(
        ({ share }: { share: IShareModal }) => {
          share
            .init()
            .then(() => {
              setReadyState(1);
              shareRef.current = share;
              resolve(true);
            })
            .catch(() => {
              setReadyState(0);
              reject();
            });
        }
      );
    });
  };

  const getShareConfig = (detail: any) => {
    const link = getLink(inviteCode.current);

    const { prizePool, prizeToken } = getPrizeInfo(detail);

    let contentDesc;
    let content;
    switch (detail?.taskType) {
      case 2:
        // 交易任务
        contentDesc = `<span>${t('shareTradeTitle', {
          total_prizepool: safeToFormat(prizePool, 0),
          token: prizeToken,
        })} </span>`;
        content = `${t('shareTradeContent', {
          total_prizepool: safeToFormat(prizePool, 0),
          token: prizeToken,
        })} ${link}`;
        break;
      case 3:
        // 交易+入金任务
        contentDesc = `<span>${t('shareAllTitle', {
          total_prizepool: safeToFormat(prizePool, 0),
          token: prizeToken,
        })} </span>`;
        content = `${t('shareAllContent', {
          total_prizepool: safeToFormat(prizePool, 0),
          token: prizeToken,
        })} ${link}`;
        break;
      default:
        // 入金任务
        contentDesc = `<span>${t('sharePart1')} ${safeToFormat(
          prizePool,
          0
        )} ${prizeToken} ${t('sharePart2')} </span>`;
        content = `${t('sharePart1')} ${safeToFormat(
          prizePool,
          0
        )} ${prizeToken} ${t('sharePart2')}. ${t('sharePart3')} ${link}`;
    }

    return {
      code: inviteCode.current,
      posterStyle: ['horizontal', 'vertical'],
      link: link,
      content: content,
      // contentLabel: t('lpn_share_text_label'),
      poster: [
        {
          // 海报模板
          template: 'default',
          // 海报数据
          templateData: {
            backgroundImage: {
              horizontal:
                '/common-static/fhs/web尺寸-ea29229afd63d01c4e99c676ec96e994.png',
              vertical:
                '/common-static/fhs/H5尺寸-28a0fae896d725c57b78c677dc0d1758.png',
            },
            // 自定义内容文案
            content: {
              desc: contentDesc,
            },
            bottom: {
              showCode: true,
            },
          },
        },
      ],
    };
  };

  const setVisible = async (detail: any) => {
    try {
      if (readyState !== 1) {
        setIsLoading(true);
        await init();
      }
      await getInviteCode();

      if (shareRef.current && inviteCode.current) {
        const { VISIBLE, CONFIG } = shareRef.current.properties;
        shareRef.current.setProperty(CONFIG, getShareConfig(detail));
        shareRef.current.setProperty(VISIBLE, true);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return {
    openShareModal: setVisible,
    shareModalInitLoading: isLoading,
  };
}
